import { motion } from 'framer-motion';
import { X, Image as ImageIcon, Box, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Tutorial Modal — 3D Prototyping Guide
 * Extracted from DesignStudio.jsx (Feb 20, 2026 refactor)
 */
export default function TutorialModal({ isOpen, onClose, language, onDontShowAgain }) {
  if (!isOpen) return null;

  const t = language === 'tr' ? {
    title: '3D Prototip Nasıl Oluşturulur?',
    subtitle: 'Basit 2 adımda fikirlerinizi 3D modele dönüştürün',
    step1Title: 'Adım 1: 2D Konsept Tasarım',
    step1Desc: 'Önce fikrinizi metin olarak girin ve AI ile 2D konsept görsel oluşturun. Bu görsel, 3D modelin temelini oluşturacak.',
    step1Tip: 'Örnek: "Akıllı ev robotu tasarla, yuvarlak gövde, LED göstergeli"',
    step2Title: 'Adım 2: 3D Prototip Oluştur',
    step2Desc: '2D tasarım hazır olduktan sonra "3D Prototip" butonuna tıklayarak 3D modelinizi oluşturun. Bu işlem 2-3 dakika sürebilir.',
    step2Tip: 'İpucu: Detaylı açıklamalar daha iyi 3D sonuçlar verir',
    proTip: 'Pro İpucu',
    proTipText: '"Pro Proje" modunu kullanarak tam proje paketi (sunum, maliyet analizi, üretim planı) da oluşturabilirsiniz.',
    gotIt: 'Anladım!',
    dontShowAgain: 'Bir daha gösterme',
  } : {
    title: 'How to Create 3D Prototype?',
    subtitle: 'Transform your ideas into 3D models in 2 simple steps',
    step1Title: 'Step 1: 2D Concept Design',
    step1Desc: 'First, enter your idea as text and create a 2D concept image with AI. This image will form the basis of the 3D model.',
    step1Tip: 'Example: "Design a smart home robot, round body, LED display"',
    step2Title: 'Step 2: Generate 3D Prototype',
    step2Desc: 'Once the 2D design is ready, click the "3D Prototype" button to create your 3D model. This may take 2-3 minutes.',
    step2Tip: 'Tip: Detailed descriptions give better 3D results',
    proTip: 'Pro Tip',
    proTipText: 'Use "Pro Project" mode to also create a full project package (presentation, cost analysis, manufacturing plan).',
    gotIt: 'Got it!',
    dontShowAgain: "Don't show again",
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      data-testid="tutorial-modal-backdrop"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        className="bg-[#060710] border border-white/10 w-full max-w-lg overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="tutorial-modal"
      >
        {/* Header */}
        <div className="relative p-6 bg-[#0A0A0A] border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 border border-white/10 flex items-center justify-center">
              <HelpCircle className="w-5 h-5 text-white/70" />
            </div>
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-white/40 mb-1">
                {language === 'tr' ? 'Rehber' : 'Tutorial'}
              </div>
              <h3 className="text-lg font-medium text-white">{t.title}</h3>
              <p className="text-sm text-white/50 mt-0.5">{t.subtitle}</p>
            </div>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={onClose}
            className="absolute top-4 right-4 text-white/40 hover:text-white"
            data-testid="tutorial-close-btn"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Steps */}
        <div className="p-6 space-y-6">
          <div className="relative" data-testid="tutorial-step-1">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-9 h-9 border border-white/20 flex items-center justify-center text-white font-mono text-sm">
                  01
                </div>
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-white mb-1 flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-white/70" />
                  {t.step1Title}
                </h4>
                <p className="text-white/60 text-sm mb-2 leading-relaxed">{t.step1Desc}</p>
                <div className="border-l-2 border-white/20 pl-3 py-1">
                  <p className="text-xs text-white/50 italic font-mono">&ldquo;{t.step1Tip}&rdquo;</p>
                </div>
              </div>
            </div>
            <div className="absolute left-[18px] top-10 w-px h-6 bg-white/10" />
          </div>

          <div data-testid="tutorial-step-2">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-9 h-9 border border-white/20 flex items-center justify-center text-white font-mono text-sm">
                  02
                </div>
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-white mb-1 flex items-center gap-2">
                  <Box className="w-4 h-4 text-white/70" />
                  {t.step2Title}
                </h4>
                <p className="text-white/60 text-sm mb-2 leading-relaxed">{t.step2Desc}</p>
                <div className="border-l-2 border-white/20 pl-3 py-1">
                  <p className="text-xs text-white/50 italic font-mono">{t.step2Tip}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#0A0A0A] border border-white/10 p-4" data-testid="tutorial-pro-tip">
            <div className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 bg-[#FF3B30] rounded-full mt-2 flex-shrink-0" />
              <div>
                <span className="text-white font-mono text-xs uppercase tracking-wider">{t.proTip}</span>
                <p className="text-white/60 text-sm mt-1 leading-relaxed">{t.proTipText}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 flex items-center justify-between bg-[#0A0A0A]">
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              onChange={(e) => e.target.checked && onDontShowAgain()}
              className="w-4 h-4 bg-transparent border border-white/30 cursor-pointer accent-white"
              data-testid="tutorial-dont-show-checkbox"
            />
            <span className="text-white/50 text-sm group-hover:text-white transition-colors">{t.dontShowAgain}</span>
          </label>
          <Button
            onClick={onClose}
            className="bg-white text-black hover:bg-white/90 rounded-none px-5"
            data-testid="tutorial-got-it-btn"
            type="button"
          >
            {t.gotIt}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}
